import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  CompanyOperatingFunds,
  CompanyOperatingFundsUpsertPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Sub-recurso de umbral / fondo operativo de empresa.
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/operating-funds`
 *   - PUT `/api/structure/companies/{companyId}/operating-funds` (multipart)
 *
 * GET típico (vacío):
 *   `{ companyId, coinId: null, files: [] }`
 *
 * PUT: `coinId` obligatorio, opcionales `mandatory` / `restrictive` /
 * `amount` / `observations`, y pares `files` + `orders`.
 */
@Injectable({ providedIn: 'root' })
export class CompanyOperatingFundsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyOperatingFunds> {
    return this.httpClient
      .get<ApiResponse<CompanyOperatingFunds | null>>(
        `${this.apiUrl}/${companyId}/operating-funds`,
      )
      .pipe(
        map((response) => ({
          companyId: response.data?.companyId || companyId,
          mandatory: response.data?.mandatory,
          restrictive: response.data?.restrictive,
          amount: response.data?.amount ?? null,
          observations: response.data?.observations ?? null,
          coinId: response.data?.coinId ?? null,
          files: Array.isArray(response.data?.files)
            ? response.data!.files
            : [],
        })),
      );
  }

  update(
    companyId: string,
    payload: CompanyOperatingFundsUpsertPayload,
  ): Observable<CompanyOperatingFunds> {
    return this.httpClient
      .put<ApiResponse<CompanyOperatingFunds | null>>(
        `${this.apiUrl}/${companyId}/operating-funds`,
        this.toFormData(payload),
      )
      .pipe(
        map((response) => ({
          companyId: response.data?.companyId || companyId,
          mandatory: response.data?.mandatory ?? payload.mandatory,
          restrictive: response.data?.restrictive ?? payload.restrictive,
          amount: response.data?.amount ?? payload.amount ?? null,
          observations:
            response.data?.observations ?? payload.observations ?? null,
          coinId: response.data?.coinId ?? payload.coinId ?? null,
          files: Array.isArray(response.data?.files)
            ? response.data!.files
            : [],
        })),
      );
  }

  private toFormData(payload: CompanyOperatingFundsUpsertPayload): FormData {
    const formData = new FormData();

    if (payload.mandatory != null) {
      formData.append('mandatory', String(payload.mandatory));
    }
    if (payload.restrictive != null) {
      formData.append('restrictive', String(payload.restrictive));
    }
    if (payload.amount != null && payload.amount !== '') {
      formData.append('amount', String(payload.amount));
    }
    if (payload.observations != null && payload.observations !== '') {
      formData.append('observations', payload.observations);
    }

    formData.append('coinId', payload.coinId);

    const files: File[] = payload.files ?? [];
    const orders: number[] = payload.orders ?? [];
    files.forEach((file, index) => {
      formData.append('files', file);
      formData.append('orders', String(orders[index] ?? index + 1));
    });

    return formData;
  }
}
